import React, { useState, useRef, useEffect } from 'react';
import { usePendingTasks } from './usePendingTasks'; // Assuming usePendingTasks is in the same 'hooks' folder

// Simple Bell Icon SVG
const BellIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
);

export default function NotificationBell({ appId, userProfile, setView }) {
    const { tasks, loading } = usePendingTasks(appId, userProfile);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleTaskClick = (route) => {
        setIsOpen(false);
        setView(route); // Using your custom view switcher!
    };

    const taskCount = tasks.length;

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-gray-500 hover:text-[#213368] transition focus:outline-none"
            >
                <BellIcon />
                {taskCount > 0 && (
                    <span className="absolute top-1 right-1 flex items-center justify-center h-4 w-4 bg-red-500 text-white text-[10px] font-bold rounded-full border border-white">
                        {taskCount > 99 ? '99+' : taskCount}
                    </span>
                )}
            </button>

            {/* Dropdown Panel */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                        <h3 className="font-bold text-[#213368]">Pending Actions</h3>
                        <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full font-medium">
                            {taskCount} Tasks
                        </span>
                    </div>

                    <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
                        {loading ? (
                            <div className="p-8 text-center text-gray-400 text-sm">Loading tasks...</div>
                        ) : tasks.length === 0 ? (
                            <div className="p-8 text-center text-gray-400 text-sm">You're all caught up! 🎉</div>
                        ) : (
                            tasks.map((task) => (
                                <div 
                                    key={task.id} 
                                    onClick={() => handleTaskClick(task.route)}
                                    className="p-4 hover:bg-blue-50 cursor-pointer transition flex gap-3 group"
                                >
                                    {/* Indicator Dot based on type */}
                                    <div className="mt-1">
                                        <span className={`block h-2 w-2 rounded-full ${
                                            task.type === 'Finance' ? 'bg-green-500' :
                                            task.type === 'HR' ? 'bg-[#20BEC6]' :
                                            'bg-orange-500'
                                        }`}></span>
                                    </div>
                                    
                                    <div className="flex-1">
                                        <p className="text-sm font-bold text-[#213368] group-hover:text-blue-700">
                                            {task.title}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                                            {task.description}
                                        </p>
                                        <p className="text-[10px] text-gray-400 mt-2 font-medium">
                                            {task.date.toLocaleDateString()} • Go to {task.type}
                                        </p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}